/**
 * Web-side spatial wiring: loads the seeded locality datasets and derives the
 * Water Plan + insight spatial context for an incident from its coordinates.
 * "Nearest hydrant" etc. are computed from seed data here — never echoed input.
 */
import bungendoreData from '@data/localities/bungendore.json';
import {
  deriveWaterContext,
  localKnowledgeNear,
  exposuresNear,
  type LocalityDataset,
  type SeededFeature,
  type WaterPlan,
  type SpatialWaterContext,
  type SpatialInsightContext,
  type NearbyNote,
  type Incident,
  type LngLat,
} from '@duty/shared';

const DATASETS: Record<string, LocalityDataset> = {
  bungendore: bungendoreData as LocalityDataset,
};

export function datasetFor(incident: Incident): LocalityDataset | undefined {
  return incident.locality ? DATASETS[incident.locality] : undefined;
}

/** All seeded features for the incident's locality (for the map). */
export function featuresFor(incident: Incident): SeededFeature[] {
  return datasetFor(incident)?.features ?? [];
}

export function attributionFor(incident: Incident): string[] {
  return datasetFor(incident)?.attribution ?? [];
}

function waterContextToPlanPatch(w: SpatialWaterContext): Partial<WaterPlan> {
  return {
    nearestHydrantDistanceM: w.nearestHydrantDistanceM,
    hydrantConfidence: w.hydrantConfidence,
    staticWaterAvailable: w.staticWaterAvailable,
    staticWaterNote:
      w.nearestStaticWaterLabel != null
        ? `${w.nearestStaticWaterLabel} (~${w.nearestStaticWaterDistanceM} m)`
        : undefined,
  };
}

/** Compute a Water Plan patch from seeded hydrant / static-water data (Bungendore demo only). */
export function deriveWaterPlanPatch(incident: Incident): Partial<WaterPlan> | undefined {
  const ds = datasetFor(incident);
  if (!ds || !incident.coordinates) return undefined;
  return waterContextToPlanPatch(deriveWaterContext(incident.coordinates, ds));
}

/**
 * Compute a Water Plan patch from a live OSM feature set (any address, not
 * just the seeded locality) — see incident/osm-water.ts for the fetch.
 */
export function waterPlanPatchFromOsmFeatures(point: LngLat, features: SeededFeature[]): Partial<WaterPlan> {
  const ds: LocalityDataset = { id: 'osm-live', name: 'Live OSM lookup', attribution: [], features };
  return waterContextToPlanPatch(deriveWaterContext(point, ds));
}

/** Spatial context passed into the rule engine (access notes, exposures…). */
export function spatialInsightContext(incident: Incident): SpatialInsightContext | undefined {
  const ds = datasetFor(incident);
  if (!ds || !incident.coordinates) return undefined;
  const notes = localKnowledgeNear(incident.coordinates, ds);
  const exposures = exposuresNear(incident.coordinates, ds);
  const water = deriveWaterContext(incident.coordinates, ds);
  return {
    accessNotes: notes.map((n) => n.label),
    exposures: exposures.map((e) => e.label),
    staticWaterLabel: water.nearestStaticWaterLabel,
    staticWaterDistanceM: water.nearestStaticWaterDistanceM,
  };
}

/** Local-knowledge notes near the incident (for the panel). */
export function localKnowledgeFor(incident: Incident): NearbyNote[] {
  const ds = datasetFor(incident);
  if (!ds || !incident.coordinates) return [];
  return localKnowledgeNear(incident.coordinates, ds, 600);
}
